/**
 * AssociatedServices
 *
 * Displays a "Additional Services" section with a grid of service cards.
 * Used at the bottom of consulting service pages to cross-promote other offerings.
 *
 * Props:
 * - subtitle (string, optional): Text displayed beneath the "Additional Services" heading.
 * - cols (number, optional): Number of grid columns on md+ screens. Accepts 2 or 3. Defaults to 3.
 * - services (array, required): Array of service card objects with the following shape:
 *     - bg (string): Tailwind background class for the card (e.g. "bg-blue", "bg-blue/20").
 *     - imageSrc (string): URL of the card image.
 *     - imageAlt (string): Alt text for the card image.
 *     - imageClassName (string): Tailwind classes controlling the image size/fit.
 *     - containerClassName (string, optional): Tailwind classes for the image container height.
 *                                              Defaults to "h-40 md:h-48 lg:h-56 xl:h-64".
 *     - label (string): Text shown in the pill label at the bottom of the card.
 */

import { Link } from "react-router-dom";

const colsClass = { 2: "md:grid-cols-2", 3: "md:grid-cols-3" };

const AssociatedServices = ({ subtitle, services, cols = 3 }) => {
  return (
    <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-[160px]">
      <div className="max-w-7xl mx-auto flex flex-col items-center">
        <img
          src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/horizontal-line.svg"
          alt=""
          className="w-28 md:w-56 mb-8 md:mb-12"
        />
        <h2 className="text-xl md:text-3xl lg:text-4xl xl:text-6xl font-bold text-blue-dark1 mb-4 text-center">
          Additional Services
        </h2>
        {subtitle && (
          <p className="text-sm md:text-base mb-12 text-center">{subtitle}</p>
        )}

        <div className={`grid grid-cols-1 ${colsClass[cols]} gap-6 w-full`}>
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.link ?? "#"}
              onClick={(e) => service.link === "#" && e.preventDefault()}
              className={`relative ${service.bg} rounded-2xl p-6 pb-8 flex flex-col items-center`}
            >
              <div className={`w-full flex items-center justify-center overflow-hidden ${service.containerClassName ?? "h-40 md:h-48 lg:h-56 xl:h-64"}`}>
                <img
                  src={service.imageSrc}
                  alt={service.imageAlt}
                  className={service.imageClassName}
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-5 left-0 right-0 bg-white border border-black rounded-full py-2 px-3 md:py-3 md:px-4 lg:py-4 lg:px-6">
                <p className="!text-sm md:!text-base font-bold text-black text-center">
                  {service.label}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AssociatedServices;
